import api from '../lib/api';
import { IGroupData, IGroupSessionEntry, SideConfig } from '@tool-no-hu/shared/types';

export async function getGroupsService(
  options: { silent?: boolean } = {},
): Promise<IGroupData[]> {
  try {
    const response = await api.get('/api/v1/groups', {
      _skipToast: options.silent,
    } as any);
    if (response.data && response.data.code === 200) {
      const list = response.data.data || [];
      return list;
    }
    return [];
  } catch (error) {
    console.error('Failed to get groups:', error);
    return [];
  }
}

export async function getGroupByIdService(
  id: string,
  options: { silent?: boolean } = {},
): Promise<IGroupData | null> {
  try {
    const response = await api.get(`/api/v1/groups/${id}`, {
      _skipToast: options.silent,
    } as any);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to get group by id:', error);
    return null;
  }
}

export async function createGroupService(name: string, gameType?: string): Promise<IGroupData | null> {
  try {
    const response = await api.post('/api/v1/groups', { name, gameType });
    if (response.data && response.data.code === 201) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to create group:', error);
    return null;
  }
}

export async function updateGroupService(id: string, updateData: Partial<IGroupData>): Promise<IGroupData | null> {
  try {
    const response = await api.put(`/api/v1/groups/${id}`, updateData);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to update group:', error);
    return null;
  }
}

export async function deleteGroupService(id: string): Promise<boolean> {
  try {
    const response = await api.delete(`/api/v1/groups/${id}`);
    if (response.data && response.data.code === 200) {
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to delete group:', error);
    return false;
  }
}

/** GET /api/v1/groups/:id/sessions — lịch sử phiên đã đánh của group (show phiên) */
export interface GroupsPaginatedResult {
  items: IGroupData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function getGroupsPaginatedService(
  page: number = 1,
  limit: number = 10,
  search?: string,
): Promise<GroupsPaginatedResult> {
  try {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    const response = await api.get('/api/v1/groups', { params });
    if (response.data && response.data.code === 200) {
      const d = response.data.data;
      return {
        items: d.groups || [],
        total: d.total || 0,
        page: d.page || page,
        limit: d.limit || limit,
        totalPages: d.totalPages || 0,
      };
    }
    return { items: [], total: 0, page, limit, totalPages: 0 };
  } catch (error) {
    console.error('Failed to get paginated groups:', error);
    return { items: [], total: 0, page, limit, totalPages: 0 };
  }
}

/**
 * Lưu toàn bộ cấu hình nhóm trong đúng 1 request: tên, game, danh sách thành viên
 * (accountId) và sideConfigs (số tiền cược mỗi side — CHAN/LE).
 *
 * Thay cho cách cũ GET group rồi PUT lại mảng members — sửa 2 tài khoản liên tiếp
 * ở cách cũ có thể ghi đè lẫn nhau vì mỗi lần lưu đều dựng lại mảng từ snapshot riêng.
 */
export async function updateGroupConfigService(
  groupId: string,
  payload: {
    name: string;
    gameType: string;
    members: string[];
    sideConfigs: SideConfig[];
  },
): Promise<IGroupData | null> {
  try {
    const response = await api.put(`/api/v1/groups/${groupId}`, payload);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    // Interceptor đã toast message từ server (VD: nhóm đang chạy không đổi được game)
    console.error('Failed to update group config:', error);
    return null;
  }
}

export async function getGroupSessionsService(
  id: string,
  options: { silent?: boolean } = {},
): Promise<IGroupSessionEntry[]> {
  try {
    const response = await api.get(`/api/v1/groups/${id}/sessions`, {
      _skipToast: options.silent,
    } as any);
    if (response.data && response.data.code === 200) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Failed to get group sessions:', error);
    return [];
  }
}

